import { isPresent } from '../isPresent';

export type HeadlineType = 'h1' | 'h2' | 'h3' | 'h4';

export type ArticleContentItem =
  | Headline
  | Bullets
  | Bullets2
  | LayeredBullets
  | GalleryImages
  | ArticleImage
  | Text
  | MPTextarea;

interface Headline {
  acf_fc_layout: 'mp_headline';
  headline_type: HeadlineType;
  headline_text: string;
}

interface LayeredBullets {
  acf_fc_layout: 'mp_bullets_test';
  bullets: Bullet[];
}

export interface SubBullet {
  sub_bullet_text: string;
}

interface Bullets {
  acf_fc_layout: 'mp_bullets';
  bullet_text: string;
}

export interface Bullet {
  bullet_text: string;
  sub_bullet?: SubBullet[];
}

interface Bullets2 {
  acf_fc_layout: 'mp_bullets2';
  bullets: Bullet[];
}

interface ArticleImage {
  acf_fc_layout: 'mp_img';
  img_single: string;
  img_array: number;
}

interface GalleryImages {
  acf_fc_layout: 'mp_gallery';
  mp_gallery_images: string[];
}

interface Text {
  acf_fc_layout: 'mp_wysiwyg';
  mp_wywsiwyg: string;
}

interface MPTextarea {
  acf_fc_layout: 'mp_textarea';
  mp_text: string;
}

type WordpressResource = {
  id: number;
  date: string;
  date_gmt: string;
  modified: string;
  modified_gmt: string;
  slug: string;
  status: string;
  type: string;
  link: string;
  categories: string[];
  subtitle: string;
  parent: number;
  marinepulse_cat: any[]; // You might want to define a more specific type based on the objects you expect in this array
  acf: {
    marinepulse_content: null | ArticleContentItem[];
  };
  _links: {
    self: Array<{ href: string }>;
    collection: Array<{ href: string }>;
    about: Array<{ href: string }>;
    'wp:attachment': Array<{ href: string }>;
    'wp:term': Array<{
      taxonomy: string;
      embeddable: boolean;
      href: string;
    }>;
    curies: Array<{
      name: string;
      href: string;
      templated: boolean;
    }>;
  };
};

// Type with title as an object containing a rendered property
export type WordpressArticle = WordpressResource & {
  title: {
    rendered: string;
  };
};

// Type with title as a string
export type WordpressReleaseNote = Omit<WordpressResource, 'title'> & {
  title: string;
};

export const getNewestReleaseNotes = (
  resources: WordpressReleaseNote[]
): WordpressReleaseNote | null => {
  // use reduce to find the newest date of the resources
  if (!resources) return null;
  return resources.reduce((newest, resource) => {
    return new Date(resource.date) > new Date(newest.date) ? resource : newest;
  }, resources[0]);
};

const filterArticleByTitle = (
  resource: WordpressArticle,
  search: string
): WordpressArticle | null => {
  if (resource.title.rendered?.toLowerCase().includes(search)) {
    return resource;
  }

  if (
    resource.categories &&
    resource.categories.some((category) =>
      category.toLowerCase().includes(search)
    )
  ) {
    return resource;
  }

  return null;
};

export const filterArticles = (
  articles: WordpressArticle[],
  search: string
) => {
  const lowercaseSearch = search.toLowerCase();
  return articles
    .map((article) => filterArticleByTitle(article, lowercaseSearch))
    .filter(isPresent);
};
